import {analyticsLogger} from 'analytics/analyticsLogger';
import {useEffect} from 'react';

const BYTES_PER_MEGABYTE = 1024 * 1024;
const MEMORY_PROFILING_BUCKET_SIZE_MB = 100;

export const MEMORY_PROFILING_INTERVAL_MS = 5 * 60 * 1000;
export const MEMORY_PROFILING_MIN_USED_HEAP_SIZE_BYTES =
  MEMORY_PROFILING_BUCKET_SIZE_MB * BYTES_PER_MEGABYTE;

type PerformanceMemory = {
  usedJSHeapSize?: number;
};

type PerformanceWithMemory = Performance & {
  memory?: PerformanceMemory;
};

type UseMemoryProfilingArgs = {
  documentId: string;
};

const getNowMs = () =>
  typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now();

const getUsedJSHeapSize = () => {
  if (typeof performance === 'undefined') return undefined;

  const memory = (performance as PerformanceWithMemory).memory;
  const usedJSHeapSize = memory?.usedJSHeapSize;

  return typeof usedJSHeapSize === 'number' && Number.isFinite(usedJSHeapSize)
    ? usedJSHeapSize
    : undefined;
};

export const getMemoryUsageBucket = (usedJSHeapSize: number) => {
  const usedHeapSizeMb = usedJSHeapSize / BYTES_PER_MEGABYTE;
  const bucketMb =
    Math.max(1, Math.floor(usedHeapSizeMb / MEMORY_PROFILING_BUCKET_SIZE_MB)) *
    MEMORY_PROFILING_BUCKET_SIZE_MB;

  return `${bucketMb}mb`;
};

export const useMemoryProfiling = ({documentId}: UseMemoryProfilingArgs) => {
  useEffect(() => {
    if (documentId === undefined) return;

    const profilingStartedAtMs = getNowMs();

    const logMemoryUsage = () => {
      const usedJSHeapSize = getUsedJSHeapSize();
      if (
        usedJSHeapSize === undefined ||
        usedJSHeapSize <= MEMORY_PROFILING_MIN_USED_HEAP_SIZE_BYTES
      ) {
        return;
      }

      analyticsLogger().logEvent('PLANNING_DOC_PERF_MEMORY_USAGE', {
        doc_id: documentId,
        time_since_page_loaded_seconds: Math.round((getNowMs() - profilingStartedAtMs) / 1000),
        used_js_heap_size_bytes: usedJSHeapSize,
        used_js_heap_size_bucket: getMemoryUsageBucket(usedJSHeapSize),
      });
    };

    logMemoryUsage();

    const intervalId = window.setInterval(logMemoryUsage, MEMORY_PROFILING_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [documentId]);
};
